import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHead } from "@/pages/admin/AdminLayout";
import { api, gbp, longDate, STATUS_STYLES, statusLabel } from "@/lib/api";

const INTAKE_LABELS = {
  reason: "Reason for visit", medical_history: "Medical history", medications: "Medications",
  allergies: "Allergies", emergency_name: "Emergency contact", emergency_phone: "Emergency phone",
};

const PatientDialog = ({ p, onClose }) => (
  <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="patient-dialog">
      {p && (
        <>
          <DialogHeader><DialogTitle>{p.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">{p.email} {p.phone && `· ${p.phone}`}</p>
          <div className="space-y-4">
            {p.history.map((h) => (
              <div key={h.id} className="rounded-xl border border-border p-4 text-sm space-y-2" data-testid={`patient-history-${h.id}`}>
                <div className="flex justify-between gap-2">
                  <p className="font-semibold">{longDate(h.date)} · {h.start} · {h.service_name}</p>
                  <span className={`text-xs capitalize rounded-full border px-2 py-0.5 ${STATUS_STYLES[h.status]}`}>{statusLabel(h.status)}</span>
                </div>
                <p className="text-muted-foreground">{h.practitioner_name} · {gbp(h.amount)}</p>
                {h.intake && (
                  <dl className="grid sm:grid-cols-2 gap-x-4 gap-y-1 pt-2 border-t border-border">
                    {Object.entries(INTAKE_LABELS).map(([k, l]) => (
                      <div key={k}><dt className="text-xs text-muted-foreground">{l}</dt><dd>{h.intake[k] || "—"}</dd></div>
                    ))}
                  </dl>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export default function Patients() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/admin/patients").then((r) => setList(r.data)); }, []);
  const shown = list.filter((p) => `${p.name} ${p.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fade-up">
      <PageHead eyebrow="Records" title="Patients & intake">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patients" className="pl-9 w-64 bg-card" data-testid="patients-search-input" />
        </div>
      </PageHead>
      <div className="rounded-2xl bg-card border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Patient</TableHead><TableHead>Bookings</TableHead><TableHead>Last visit</TableHead><TableHead className="text-right">Total paid</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((p) => (
              <TableRow key={p.patient_id} className="cursor-pointer" onClick={() => setOpen(p)} data-testid={`patient-row-${p.patient_id}`}>
                <TableCell><p className="font-medium">{p.name}</p><p className="text-xs text-muted-foreground">{p.email}</p></TableCell>
                <TableCell>{p.bookings}</TableCell>
                <TableCell>{longDate(p.last_visit)}</TableCell>
                <TableCell className="text-right font-semibold">{gbp(p.total_spent)}</TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8" data-testid="patients-empty">No patients yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <PatientDialog p={open} onClose={() => setOpen(null)} />
    </div>
  );
}
